/**
 * Provenance helpers for Rx form fields.
 *
 * Some fields shown on an Rx form live in two places in our data model:
 *  - the Rx entity itself (what was actually saved on this prescription), and
 *  - the patient record (the patient's current master data).
 *
 * Examples: Versichertennummer (insuree number), Kostenträger / insurer id,
 * insurance status. For these, the component must be able to tell whether the
 * value it is showing came from the Rx or was inherited from the patient record,
 * so the UI can make that distinction transparent to the user (and so future
 * "copy from patient" logic can be explicit rather than silent).
 *
 * Other fields (patient name, date of birth) are NOT part of the Rx entity at
 * all. Those must be read directly from the patient record and should never be
 * routed through this helper or snapshotted into the Rx form state.
 */

export type ValueSource = "rx" | "patient" | "none";

export interface SourcedValue<T> {
  /** Effective value to display/use: the Rx-native value, falling back to the patient record. */
  value: T | undefined;
  /** Which record `value` originated from, for provenance-aware UI. */
  source: ValueSource;
}

const isPresent = (value: unknown): boolean =>
  value !== undefined && value !== null && value !== "";

/**
 * Resolve a dual-sourced field. The Rx-native value wins when present; otherwise
 * we fall back to the patient record. `source` records where the value came from
 * so callers can distinguish Rx-saved data from data inherited from the patient.
 */
export function resolveSourcedValue<T>(
  rxValue: T | null | undefined,
  patientValue: T | null | undefined,
): SourcedValue<T> {
  if (isPresent(rxValue)) return { value: rxValue as T, source: "rx" };
  if (isPresent(patientValue)) return { value: patientValue as T, source: "patient" };
  return { value: (rxValue ?? patientValue ?? undefined) as T | undefined, source: "none" };
}
